import React from 'react';
import { Box, Typography, Checkbox, CircularProgress } from '@mui/material';
import { useTranslate } from 'src/locales';
import { useBaseQuery } from 'src/api/core/useBaseQuery';
import dayjs from 'dayjs';

interface BlondDetailProps {
  onClose: () => void;
  srnId?: number;
  udId?: number;
}

const BlondDetail: React.FC<BlondDetailProps> = ({ onClose, srnId, udId }) => {
  const { t } = useTranslate();

  // API isteği - useBaseQuery doğrudan response.data döndürür
  const { data: apiData, isLoading } = useBaseQuery<any>({
    endpoint: (srnId && udId)
      ? `/nurse/api/Order/GetOtherTreatmentsDetail?srnId=${srnId}&udId=${udId}`
      : '',
    queryKey: ['blood-treatment-detail', srnId, udId],
    options: {
      enabled: !!srnId && !!udId,
      staleTime: 0,
    },
  });

  // API doğrudan obje veya dizi döndürebilir
  const detailData = Array.isArray(apiData) ? apiData[0] : apiData;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400, width: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '533px',
        boxShadow: '4px 4px 25px rgba(0, 0, 0, 0.25)',
        borderRadius: '20px',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'flex-start',
        p: 1.875,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1.875,
        }}
      >
        {/* Kan Ürünü Adı */}
        <Box
          sx={{
            minHeight: 39,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: -0.01,
              lineHeight: 1.4,
            }}
          >
            {detailData?.name || '-'}
          </Typography>
        </Box>

        {/* Genel Bilgiler */}
        <Box
          sx={{
            width: '100%',
            boxShadow: '0px 4px 4px rgba(120, 120, 120, 0.25)',
            borderRadius: '20px',
            backgroundColor: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            p: 1.25,
            gap: 0.375,
          }}
        >
          <Box
            sx={{
              width: '100%',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'flex-start',
              px: 2.5,
              py: 1.15,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: -0.01,
                lineHeight: '22px',
              }}
            >
              {t('nurse.order-medicine.detail.general-info')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              fontSize: 12,
              color: '#767a85',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                flexWrap: 'wrap',
              }}
            >
              {/* Plan Saati */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.plan-time')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.startdate ? dayjs(detailData.startdate).format('HH:mm') : '-'}
                </Typography>
              </Box>

              {/* Kan Grubu */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.blood-group')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.bloodgroupname || '-'}
                </Typography>
              </Box>

              {/* Başlangıç Tarihi */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.start-time')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.startdate ? dayjs(detailData.startdate).format('DD.MM.YYYY') : '-'}
                </Typography>
              </Box>

              {/* Bitiş Tarihi */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.end-time')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.finishdate ? dayjs(detailData.finishdate).format('DD.MM.YYYY') : '-'}
                </Typography>
              </Box>

              {/* Miktar */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.card-fields.amount')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.quantity ?? '-'}
                </Typography>
              </Box>

              {/* Yapıldı mı */}
              <Box
                sx={{
                  height: 42,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.done')}
                </Typography>
                <Checkbox
                  size="small"
                  checked={detailData?.isdone === '1' || detailData?.isdone === 1}
                  disabled
                  sx={{
                    width: 25,
                    height: 28,
                    p: 0,
                  }}
                />
              </Box>

              {/* İptal */}
              <Box
                sx={{
                  height: 42,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.cancel')}
                </Typography>
                <Checkbox
                  size="small"
                  checked={detailData?.iscancel === '1' || detailData?.iscancel === 1}
                  disabled
                  sx={{
                    width: 25,
                    height: 28,
                    p: 0,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Notlar */}
        <Box
          sx={{
            width: '100%',
            boxShadow: '0px 4px 4px rgba(120, 120, 120, 0.25)',
            borderRadius: '20px',
            backgroundColor: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            p: 1.25,
            gap: 0.375,
          }}
        >
          <Box
            sx={{
              width: '100%',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'flex-start',
              px: 2.5,
              py: 1.15,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: -0.01,
                lineHeight: '22px',
              }}
            >
              {t('nurse.order-medicine.detail.notes')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              fontSize: 12,
              color: '#767a85',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                flexWrap: 'wrap',
              }}
            >
              {/* Açıklama */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: -0.02,
                    lineHeight: '18px',
                    color: '#767a85',
                  }}
                >
                  {t('nurse.order-medicine.detail.description')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    letterSpacing: -0.01,
                    lineHeight: '22px',
                    fontWeight: 600,
                    color: '#1f2026',
                  }}
                >
                  {detailData?.name || '-'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Süre / Onay */}
        <Box
          sx={{
            width: '100%',
            boxShadow: '0px 4px 4px rgba(120, 120, 120, 0.25)',
            borderRadius: '20px',
            backgroundColor: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            p: 1.25,
            gap: 0.375,
          }}
        >
          <Box
            sx={{
              width: '100%',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'flex-start',
              px: 2.5,
              py: 1.15,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: -0.01,
                lineHeight: '22px',
              }}
            >
              {t('nurse.order-medicine.detail.duration-approval')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              fontSize: 12,
              color: '#767a85',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 2.5,
                }}
              >
                {/* Uygulama Tarihi */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      letterSpacing: -0.02,
                      lineHeight: '18px',
                      color: '#767a85',
                    }}
                  >
                    {t('nurse.order-medicine.detail.application-date')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      letterSpacing: -0.01,
                      lineHeight: '22px',
                      fontWeight: 600,
                      color: '#1f2026',
                    }}
                  >
                    {detailData?.appdate ? dayjs(detailData.appdate).format('DD.MM.YYYY') : '-'}
                  </Typography>
                </Box>

                {/* Uygulama Saati */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      letterSpacing: -0.02,
                      lineHeight: '18px',
                      color: '#767a85',
                    }}
                  >
                    {t('nurse.order-medicine.detail.application-time-full')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      letterSpacing: -0.01,
                      lineHeight: '22px',
                      fontWeight: 600,
                      color: '#1f2026',
                    }}
                  >
                    {detailData?.appdate ? dayjs(detailData.appdate).format('HH:mm') : '-'}
                  </Typography>
                </Box>

                {/* İşlem Zamanı */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      letterSpacing: -0.02,
                      lineHeight: '18px',
                      color: '#767a85',
                    }}
                  >
                    {t('nurse.order-medicine.detail.processor')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      letterSpacing: -0.01,
                      lineHeight: '22px',
                      fontWeight: 600,
                      color: '#1f2026',
                    }}
                  >
                    {detailData?.srudctime ? dayjs(detailData.srudctime).format('DD.MM.YYYY HH:mm') : '-'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BlondDetail;
